import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ImagePlus, X, Star, GripVertical, Link2 } from "lucide-react";
import { api, formatApiErrorDetail, resolveImageUrl } from "@/lib/api";

export const PhotoManager = ({ images, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [dragIndex, setDragIndex] = useState(null);

  const uploadFiles = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    try {
      const data = new FormData();
      Array.from(fileList).forEach((f) => data.append("files", f));
      const res = await api.post("/admin/upload", data, { headers: { "Content-Type": "multipart/form-data" } });
      onChange([...images, ...res.data.urls]);
      toast.success(`${res.data.urls.length} image(s) uploaded`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const addUrl = () => {
    const u = urlInput.trim();
    if (!u) return;
    onChange([...images, u]);
    setUrlInput("");
  };

  const remove = (i) => onChange(images.filter((_, idx) => idx !== i));

  const makePrimary = (i) => {
    if (i === 0) return;
    const next = [...images];
    const [item] = next.splice(i, 1);
    onChange([item, ...next]);
    toast.success("Primary photo updated");
  };

  const onDrop = (i) => {
    if (dragIndex === null || dragIndex === i) return;
    const next = [...images];
    const [item] = next.splice(dragIndex, 1);
    next.splice(i, 0, item);
    onChange(next);
    setDragIndex(null);
  };

  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-wider text-zinc-500 block mb-2">Vehicle Photos (first image is the primary photo)</label>
      <label
        className="w-full border-2 border-dashed border-zinc-300 hover:border-[#FF6B00] rounded-sm py-6 flex flex-col items-center gap-2 text-zinc-500 hover:text-[#FF6B00] transition-colors cursor-pointer"
        data-testid="vf-upload-button"
      >
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          className="hidden"
          onChange={(e) => {
            uploadFiles(e.target.files);
            e.target.value = "";
          }}
          data-testid="vf-file-input"
        />
        {uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <ImagePlus className="h-6 w-6" />}
        <span className="text-xs font-bold uppercase tracking-wider">
          {uploading ? "Uploading..." : "Upload Photos (JPG, PNG, WEBP - max 10MB each)"}
        </span>
      </label>

      <div className="flex gap-2 mt-3">
        <input
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          placeholder="Or paste an image URL"
          className="h-10 flex-1 rounded-sm border border-zinc-300 bg-white px-3 text-sm text-zinc-800 focus:outline-none focus:ring-1 focus:ring-[#FF6B00] font-mono text-xs"
          data-testid="vf-url-input"
        />
        <button type="button" onClick={addUrl} className="h-10 px-4 border border-zinc-300 text-zinc-600 hover:border-[#FF6B00] hover:text-[#FF6B00] rounded-sm text-xs font-bold uppercase tracking-wider" data-testid="vf-add-url-button">
          <Link2 className="h-3.5 w-3.5 inline mr-1" /> Add URL
        </button>
      </div>

      {images.length > 0 && (
        <div className="flex flex-wrap gap-3 mt-4" data-testid="vf-image-previews">
          {images.map((url, i) => (
            <div
              key={`${url}-${i}`}
              draggable
              onDragStart={() => setDragIndex(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(i)}
              className={`relative w-28 h-20 rounded-sm overflow-hidden border ${i === 0 ? "border-[#FF6B00]" : "border-zinc-300"} cursor-grab`}
              data-testid={`vf-photo-${i}`}
            >
              <img src={resolveImageUrl(url)} alt={`Vehicle ${i + 1}`} className="w-full h-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-black/40 flex items-center justify-between px-1">
                <GripVertical className="h-3 w-3 text-zinc-500" />
                <div className="flex">
                  {i !== 0 && (
                    <button type="button" onClick={() => makePrimary(i)} className="p-1 text-zinc-500 hover:text-[#FF6B00]" aria-label={`Make photo ${i + 1} primary`} title="Make primary" data-testid={`vf-primary-${i}`}>
                      <Star className="h-3 w-3" />
                    </button>
                  )}
                  <button type="button" onClick={() => remove(i)} className="p-1 text-zinc-500 hover:text-red-600" aria-label={`Remove photo ${i + 1}`} data-testid={`vf-remove-image-${i}`}>
                    <X className="h-3 w-3" />
                  </button>
                </div>
              </div>
              {i === 0 && (
                <span className="absolute top-1 left-1 bg-[#FF6B00] text-black text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm">Primary</span>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="text-[11px] text-zinc-400 mt-2">Drag photos to reorder. The first photo is used as the primary image everywhere.</p>
    </div>
  );
};
